import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import { HomePage } from './pages/HomePage'
import { NotFound } from './pages/NotFound'

// Route-level code splitting. The landing page and the 404 fallback stay
// eager so first paint is unaffected; every heavier page is fetched on
// demand. These pages use named exports, so we map each onto the
// default-export shape that React.lazy expects.
const Collections = lazy(() => import('./pages/Collections').then(m => ({ default: m.Collections })))
const CollectionEntry = lazy(() => import('./pages/CollectionEntry').then(m => ({ default: m.CollectionEntry })))
const MyNFPTs = lazy(() => import('./pages/MyNFPTs').then(m => ({ default: m.MyNFPTs })))
const NFPTDetail = lazy(() => import('./pages/NFPTDetail').then(m => ({ default: m.NFPTDetail })))
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })))
const CreateNFPT = lazy(() => import('./pages/CreateNFPT').then(m => ({ default: m.CreateNFPT })))
const CreateCollection = lazy(() => import('./pages/CreateCollection').then(m => ({ default: m.CreateCollection })))
const Marketplace = lazy(() => import('./pages/AutoPurchase').then(m => ({ default: m.Marketplace })))
const ZonesExplorer = lazy(() => import('./pages/ZonesExplorer').then(m => ({ default: m.ZonesExplorer })))
const Bridge = lazy(() => import('./pages/Bridge').then(m => ({ default: m.Bridge })))
const PrivacyPunks = lazy(() => import('./pages/PrivacyPunks').then(m => ({ default: m.PrivacyPunks })))
const BlackCats = lazy(() => import('./pages/BlackCats').then(m => ({ default: m.BlackCats })))
const HowItWorks = lazy(() => import('./pages/HowItWorks').then(m => ({ default: m.HowItWorks })))
const Docs = lazy(() => import('./pages/Docs').then(m => ({ default: m.Docs })))
const Terms = lazy(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/legal/Privacy').then(m => ({ default: m.Privacy })))

// Lightweight fallback shown only while a route chunk is fetched. Mirrors the
// in-app spinner styling so lazy navigation feels native.
function RouteFallback() {
	return (
		<div className="flex items-center justify-center py-24" role="status" aria-live="polite">
			<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			<span className="sr-only">Loading…</span>
		</div>
	)
}

function App() {
	return (
		<Layout>
			<Suspense fallback={<RouteFallback />}>
				<Routes>
					<Route path="/" element={<HomePage />} />
					{/* /gallery was a redundant catch-all view of every NFPT.
					   Discovery is now collection-led; redirect existing
					   bookmarks to /collections so we don't break inbound links. */}
					<Route path="/gallery" element={<Navigate to="/collections" replace />} />
					<Route path="/collections" element={<Collections />} />
					{/* CollectionCard links to /collections/<slug-or-_id>; the entry
					   route resolves either form against the API and forwards the
					   user to the dedicated manifest mint page. */}
					<Route path="/collections/:idOrSlug" element={<CollectionEntry />} />
					<Route path="/privacy-punks" element={<PrivacyPunks />} />
					<Route path="/black-cats" element={<BlackCats />} />
					<Route path="/how-it-works" element={<HowItWorks />} />
					<Route path="/how-it-works/:txid" element={<HowItWorks />} />
					<Route path="/docs" element={<Docs />} />
					<Route path="/docs/:slug" element={<Docs />} />
					<Route path="/profile" element={<Profile />} />
					{/* Owner-scoped "what do I own?" view; the user pastes their
					   shielded address and the page fetches matches via the
					   privacy-redacted owned-items endpoint. From here they can
					   list items for sale or cancel listings. */}
					<Route path="/my-nfpts" element={<MyNFPTs />} />
					{/* Public, shareable single-item view. NFPTCard links here;
					   before this route existed every click fell through to the
					   global 404. */}
					<Route path="/nfpt/:cid" element={<NFPTDetail />} />
					<Route path="/create" element={<CreateNFPT />} />
					<Route path="/create-collection" element={<CreateCollection />} />
					<Route path="/marketplace" element={<Marketplace />} />
					<Route path="/zones" element={<ZonesExplorer />} />
					{/* The explorer originally lived at /zns; the profile is now
					   named ZINC Zones, so keep old links working. */}
					<Route path="/zns" element={<Navigate to="/zones" replace />} />
					<Route path="/bridge" element={<Bridge />} />
					<Route path="/terms" element={<Terms />} />
					<Route path="/privacy" element={<Privacy />} />
					<Route path="*" element={<NotFound />} />
				</Routes>
			</Suspense>
		</Layout>
	)
}

export default App 
